
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";
import { ASSET_CLASS_COLORS } from "@/utils/colors";
import { formatCurrency } from "@/lib/portfolioCalculations";
import { PlusCircle, CheckCircle, CircleDashed } from "lucide-react";
import { Portfolio } from "@/types/portfolio";
import { PIE_CHART_LABELS, standardAssetClasses, determineStandardAssetClass } from "@/utils/asset-class";
import { isPortfolioCompleted } from "@/utils/portfolioStatus";

interface PortfolioCardProps {
  portfolio: Portfolio;
}

export function PortfolioCard({ portfolio }: PortfolioCardProps) {
  const navigate = useNavigate();

  // Log incoming portfolio allocations to debug
  // console.log('PortfolioCard - Allocations for portfolio', portfolio.id, portfolio.name, 
  //   portfolio.portfolio_allocations?.length || 0, 'allocations');
  
  // Log the actual allocations for detailed debugging
  // if (portfolio.portfolio_allocations?.length) {
  //   console.log('Portfolio allocations detail:', JSON.stringify(portfolio.portfolio_allocations));
  // }
  
  // Type guard to safely access properties
  type EnhancedAllocation = {
    allocation_percentage: number;
    id?: string;
    asset_id?: string;
    fund_id?: string; // Added for backward compatibility with old schema
    asset_type?: string;
    fund?: {
      asset_class?: string;
      id?: string;
      fund_name?: string;
    }
  };
  
  // Group allocations by asset class (stocks, bonds, etc) with fallback for missing data
  const typeAllocations = (portfolio.portfolio_allocations || []).reduce((acc, curr: EnhancedAllocation) => {
    // Skip if missing required data
    if (!curr || typeof curr.allocation_percentage !== 'number') {
      // console.warn('Skipping invalid allocation:', curr);
      return acc;
    }
    
    // Extract asset info for debugging
    const assetId = curr.asset_id || curr.fund_id || 'unknown';
    const assetType = curr.asset_type || 'unknown';
    const fundData = curr.fund || {};
    const percentage = curr.allocation_percentage;
    
    // console.log(`Processing allocation: ${curr.id || 'unknown'}`);
    // console.log(`- Asset ID: ${assetId}`);
    // console.log(`- Asset Type: ${assetType}`);
    // console.log(`- Fund:`, fundData);
    // console.log(`- Percentage: ${percentage}%`);
    
    // Normalize to standard asset class used across app
    let type = determineStandardAssetClass(curr?.fund?.asset_class, curr?.asset_type) || 'unclassified';
    // Merge 'unclassified' into 'multi_asset' for portfolio card display to match Build view
    if (type === 'unclassified') type = 'multi_asset';
    // console.log(`- Asset class determined: ${type}`);
    
    // Accumulate under normalized class (including 'unclassified')
    acc[type] = (acc[type] || 0) + percentage;
    // console.log(`- Running total for ${type}: ${acc[type]}%`);
    
    return acc;
  }, {} as Record<string, number>);
  
  // Log final type allocations (commented out for production)
  // console.log('Final type allocations:', typeAllocations);

  // Convert to pie chart data format with proper ordering (Canadian terminology)
  const assetTypes = ['equity', 'fixed_income', 'cash', 'alternative', 'multi_asset'];
  let pieData = (assetTypes || [])
    // Include 'other' category in the display to show unclassified allocations
    .map(type => ({
      name: PIE_CHART_LABELS[type],
      value: typeAllocations[type] || 0,
      color: ASSET_CLASS_COLORS[type],
      type // keep original type for color mapping
    }));
  
  // Check if we have any allocations
  const hasAllocations = (pieData || []).some(item => item.value > 0);
  // console.log('Portfolio has allocations:', hasAllocations);
  
  if (hasAllocations) {
    // Filter out zero values for cleaner visualization
    pieData = (pieData || []).filter(item => item.value > 0);
  } else {
    // For empty portfolios, just use the empty placeholder data
    // Don't pre-allocate any percentages for new portfolios
    pieData = [];
  }

  const totalAllocation = (pieData || []).reduce((sum, item) => sum + item.value, 0);
  // Use shared completion helper for consistency; do NOT trust DB status alone (can be stale)
  const isCompleted = isPortfolioCompleted(portfolio);

  // Create placeholder data for empty state
  const placeholderData = [{
    name: "Empty",
    value: 1,
    color: "#F1F1F1" // Light grey color for placeholder
  }];

  const handleClick = () => {
    navigate(`/portfolios/build/${portfolio.id}`);
  };

  // Custom legend renderer to style the indicators
  const renderLegend = (props: any) => {
    const { payload } = props;
    
    return (
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 px-2">
        {(payload || []).map((entry: any, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-1 text-xs">
            <div 
              className="h-2 w-2 shrink-0 rounded-[2px]"
              style={{ backgroundColor: entry.color }}
            />
            <span className="truncate">
              {entry.payload.name}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card 
      className="hover:shadow-lg transition-shadow cursor-pointer overflow-hidden relative"
      onClick={handleClick}
    >
      {/* Left-side colored indicator based on allocation status */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 ${isCompleted ? 'bg-green-600' : 'bg-gray-400'}`}
      />
      <CardHeader className="relative pb-2 pt-4">
        <div className="absolute right-4 top-4">
          {isCompleted ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <CircleDashed className="h-5 w-5 text-gray-500" />
          )}
        </div>
        <CardTitle className="text-lg font-bold truncate mt-4">{portfolio.name}</CardTitle>
      </CardHeader>
      <CardContent className="pb-4">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Portfolio Value</span>
            <span className="text-sm font-medium">
              {formatCurrency(portfolio.portfolio_size || 100000)}
            </span>
          </div>
          
          <div className="flex flex-col items-center py-2">
            <PieChart width={200} height={200} className="mx-auto">
              <Pie
                data={totalAllocation === 0 ? placeholderData : pieData}
                cx={100}
                cy={80}
                innerRadius={50}
                outerRadius={70}
                paddingAngle={totalAllocation === 0 ? 0 : 2}
                dataKey="value"
                startAngle={90}
                endAngle={450}
                nameKey="name"
              >
                {(totalAllocation === 0 ? placeholderData : pieData).map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`}
                    fill={entry.color}
                    opacity={entry.value === 0 ? 0.3 : 1}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => {
                  // Format the value as percentage
                  const numValue = typeof value === 'number' ? value : parseFloat(value as string);
                  return [isNaN(numValue) ? value : `${numValue.toFixed(1)}%`, props.payload.name];
                }}
                contentStyle={{ 
                  borderRadius: '4px', 
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  padding: '8px 12px',
                  backgroundColor: 'white'
                }}
                itemStyle={{ fontSize: '12px' }}
              />
              {totalAllocation > 0 && (
                <Legend
                  content={renderLegend}
                  verticalAlign="bottom"
                  height={36}
                />
              )}
            </PieChart>
          </div>
          
          <div className="flex justify-center">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/portfolios/build/${portfolio.id}`);
              }}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              {totalAllocation === 0 ? 'Add Assets' : 'Modify Portfolio'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
